import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { getFirestore, where, getDocs, collection, query } from "firebase/firestore";

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';

import { DateRangePicker } from "../../components/DatePicker/DatePicker";
import { Header } from "../../components/Header/Header";
import { Employee, TimeEvent } from "../../types/employee";
import { useUser } from "../../context/UserContext";
import { useDateRange } from "../../context/DateRangeContext";
import { calculateWorkedMinutesWithOvernights } from "../../utils/utils";
import { PtoRequest } from "../../types/adjustments";

dayjs.extend(isoWeek);

const db = getFirestore();

type EmployeeWorkedMap = {
    [employeeId: string]: {
        [date: string]: number; //day date, total minutes worked that day (including overnights)
    };
};

type MinutesWorkedData = {
    totalMinutes: number;
    regularMinutes: number;
    overtimeMinutes: number;
};

const calculatePtoHoursInRange = (
    request: PtoRequestWithId,
    employee: Employee | undefined,
    rangeStartValue: string,
    rangeEndValue: string
) => {
    const requestStart = dayjs(request.startDate);
    const requestEnd = dayjs(request.endDate);
    const rangeStart = dayjs(rangeStartValue);
    const rangeEnd = dayjs(rangeEndValue);

    if (
        !requestStart.isValid()
        || !requestEnd.isValid()
        || requestEnd.isBefore(requestStart, "day")
        || requestEnd.isBefore(rangeStart, "day")
        || requestStart.isAfter(rangeEnd, "day")
    ) {
        return 0;
    }

    const approvedHours = typeof request.approvedHours === "number"
        ? request.approvedHours
        : Number(request.approvedDays ?? 0) * (employee?.ptoTakenHoursPerDay ?? 0);

    const requestDays = requestEnd.diff(requestStart, "day") + 1;
    const overlapStart = requestStart.isAfter(rangeStart, "day") ? requestStart : rangeStart;
    const overlapEnd = requestEnd.isBefore(rangeEnd, "day") ? requestEnd : rangeEnd;
    const overlappingDays = overlapEnd.diff(overlapStart, "day") + 1;

    // PTO requests store one aggregate hours value, so split it evenly only when
    // a request crosses the edge of the selected payroll range.
    return approvedHours * (overlappingDays / requestDays);
};

const formatPtoDays = (days: number) => {
    return Number.isInteger(days) ? days.toString() : days.toFixed(2);
};

interface PtoRequestWithId extends PtoRequest {
    id: string; // Firestore doc ID
}


export const Payroll: React.FC = () => {

    dayjs.extend(isoWeek);
    dayjs.extend(weekday);

    //State variables to manage stuff

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [potentialIssueWithUserEntries, setPotentialIssueWithUserEntries] = useState<string[]>([])
    const [workedMinutesByEmployee, setWorkedMinutesByEmployee] = useState<EmployeeWorkedMap>({});
    const [loadingHours, setLoadingHours] = useState(false);
    const { dateRange, setDateRange } = useDateRange();
    const [ptoDaysByEmployee, setPtoDaysByEmployee] = useState<Record<string, number>>({});
    const [ptoHoursByEmployee, setPtoHoursByEmployee] = useState<Record<string, number>>({});
    const [ptoHoursInRangeByEmployee, setPtoHoursInRangeByEmployee] = useState<Record<string, number>>({});
    const [loadingPtoInRange, setLoadingPtoInRange] = useState(false);
    const { userData } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const snapshot = await getDocs(collection(db, "employees"));
                let employeesList: Employee[] = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Employee[];
                employeesList = employeesList
                    .filter(emp => !emp.archived) // only active employees
                    .sort((a, b) => {
                        const lastNameComparison = a.lastName.localeCompare(b.lastName, undefined, { sensitivity: "base" });

                        if (lastNameComparison !== 0) {
                            return lastNameComparison;
                        }

                        return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: "base" });
                    });
                setEmployees(employeesList);
            } catch (error) {
                console.error("Error fetching employees:", error);
            }
        };

        fetchEmployees();
    }, []);

    useEffect(() => {
        if (!userData) return;

        if (!userData.admin) {
            navigate("/");
            return;
        }

        // Reset issues when date range changes
        setPotentialIssueWithUserEntries([]);

        const fetchAllWorkedTime = async () => {
            if (!dateRange.startDate || !dateRange.endDate) return;

            try {
                setLoadingHours(true);

                const start = dateRange.startDate.format("YYYY-MM-DD");
                const endPlusOne = dateRange.endDate.add(1, "day").format("YYYY-MM-DD");
                const endReal = dateRange.endDate.format("YYYY-MM-DD");

                const q = query(
                    collection(db, "time_entries"),
                    where("date", ">=", start),
                    where("date", "<=", endPlusOne)
                );

                const snapshot = await getDocs(q);

                const eventsByUser: Record<string, TimeEvent[]> = {};
                const newIssues: string[] = []; // temp array to track issues

                snapshot.docs.forEach((doc) => {
                    const data = doc.data();
                    const userId = data.userId;
                    const events: TimeEvent[] = Array.isArray(data.events) ? data.events : [];
                    const isToday = dayjs().format("YYYY-MM-DD") === data.date;



                    if (events.length % 2 !== 0) {
                        if (!isToday) newIssues.push(userId); // push to local array
                    }

                    if (!eventsByUser[userId]) eventsByUser[userId] = [];
                    eventsByUser[userId].push(...events);
                });

                // Update state once
                setPotentialIssueWithUserEntries(newIssues);

                const results: Record<string, Record<string, number>> = {};

                for (const [userId, events] of Object.entries(eventsByUser)) {
                    const allMinutes = calculateWorkedMinutesWithOvernights(events);
                    results[userId] = Object.fromEntries(
                        Object.entries(allMinutes).filter(([day]) => day >= start && day <= endReal)
                    );
                }

                setWorkedMinutesByEmployee(results);

            } catch (error) {
                console.error("Error fetching worked minutes:", error);
            } finally {
                setLoadingHours(false);
            }
        };

        fetchAllWorkedTime();
    }, [userData, navigate, dateRange.startDate, dateRange.endDate]);


    useEffect(() => {
        if (!userData?.admin || employees.length === 0) return;

        const fetchPtoForCurrentYear = async () => {

            try {
                const currentYear = new Date().getFullYear();
                const startOfYear = `${currentYear}-01-01`;
                const startOfNextYear = `${currentYear + 1}-01-01`;

                const q = query(
                    collection(db, "pto_requests"),
                    where("startDate", ">=", startOfYear),
                    where("startDate", "<", startOfNextYear)
                );

                const snapshot = await getDocs(q);

                const approvedRequests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PtoRequestWithId)).filter(req => req.status?.toLowerCase() === "approved");

                const daysByUser: Record<string, number> = {};
                const hoursByuser: Record<string, number> = {};

                approvedRequests.forEach(req => {
                    const currentEmp = employees.find(emp => emp.id === req.userId);
                    const daysTaken = req.approvedDays //when the pto is approved the user dictates how many days
                    //it counts as and that is this value
                    const hoursTaken = typeof req.approvedHours === "number"
                        ? req.approvedHours
                        : daysTaken * (currentEmp?.ptoTakenHoursPerDay ?? 0);

                    if (!daysByUser[req.userId]) {
                        daysByUser[req.userId] = 0;
                        hoursByuser[req.userId] = 0;
                    }
                    daysByUser[req.userId] += daysTaken;
                    hoursByuser[req.userId] += hoursTaken;
                    
                });
                setPtoDaysByEmployee(daysByUser);
                setPtoHoursByEmployee(hoursByuser);

            } catch (error) {
                console.error("Error fetching PTO requests:", error);
            }
        };

        fetchPtoForCurrentYear();
    }, [employees, userData?.admin]);

    useEffect(() => {
        if (
            !userData?.admin
            || employees.length === 0
            || !dateRange.startDate
            || !dateRange.endDate
        ) return;

        const fetchPtoForDateRange = async () => {
            const start = dateRange.startDate?.format("YYYY-MM-DD");
            const end = dateRange.endDate?.format("YYYY-MM-DD");
            if (!start || !end) return;

            try {
                setLoadingPtoInRange(true);

                const ptoQuery = query(
                    collection(db, "pto_requests"),
                    where("startDate", "<=", end)
                );
                const snapshot = await getDocs(ptoQuery);
                const hoursByUser: Record<string, number> = {};

                snapshot.docs.forEach((requestDocument) => {
                    const request = {
                        id: requestDocument.id,
                        ...requestDocument.data(),
                    } as PtoRequestWithId;

                    if (
                        request.status?.toLowerCase() !== "approved"
                        || request.endDate < start
                    ) {
                        return;
                    }

                    const employee = employees.find(emp => emp.id === request.userId);
                    const hoursInRange = calculatePtoHoursInRange(request, employee, start, end);
                    hoursByUser[request.userId] = (hoursByUser[request.userId] ?? 0) + hoursInRange;
                });

                setPtoHoursInRangeByEmployee(hoursByUser);
            } catch (error) {
                console.error("Error fetching PTO for payroll date range:", error);
                setPtoHoursInRangeByEmployee({});
            } finally {
                setLoadingPtoInRange(false);
            }
        };

        fetchPtoForDateRange();
    }, [dateRange.startDate, dateRange.endDate, employees, userData?.admin]);


    function calculateOvertime(
        empId: string,
        dailyBreakdown: { [date: string]: number } | null | undefined
    ): MinutesWorkedData {
        let totalRegular = 0;
        let totalOvertime = 0;

        if (!dailyBreakdown || Object.keys(dailyBreakdown).length === 0) {
            return {
                totalMinutes: 0,
                regularMinutes: 0,
                overtimeMinutes: 0,
            };
        }

        // Group daily totals into weeks
        const weeklyTotals: Record<string, number> = {};

        for (const date of Object.keys(dailyBreakdown)) {
            const minutes = dailyBreakdown[date] || 0; // handle missing values
            const weekStart = dayjs(date).startOf("week").format("YYYY-MM-DD");

            if (!weeklyTotals[weekStart]) weeklyTotals[weekStart] = 0;
            weeklyTotals[weekStart] += minutes;
        }

        // Apply weekly cap logic
        for (const weekStart of Object.keys(weeklyTotals)) {
            const minutes = weeklyTotals[weekStart];

            if (minutes > 2400) {
                totalRegular += 2400;
                totalOvertime += minutes - 2400;
            } else {
                totalRegular += minutes;
            }
        }

        return {
            totalMinutes: totalRegular + totalOvertime,
            regularMinutes: totalRegular,
            overtimeMinutes: totalOvertime,
        };
    }

    return (
        <div className="payroll-container">
            <Header />
            <div className="date-picker">
                <DateRangePicker
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onChange={setDateRange}
                />
            </div>

            <main className="payroll-content">
                <section className="panel">
                    <h3>Worked Hours Summary</h3>

                    {loadingHours || loadingPtoInRange ? (
                        <p>Calculating hours...</p>
                    ) : (
                        <div className="hours-summary-grid">
                            {employees.map((emp) => {
                                const employeeId = emp.id.trim();
                                const breakdown = workedMinutesByEmployee[employeeId]

                                const workedData = calculateOvertime(emp.id, breakdown)

                                const totalRegular = workedData.regularMinutes
                                const totalOvertime = workedData.overtimeMinutes
                                let styles = "employee-summary"
                                if (potentialIssueWithUserEntries.includes(emp.id)) {
                                    styles = "employee-summary issue-highlight"
                                }
                                return (
                                    <div key={emp.id} className={styles} onClick={() => navigate(`/employees/${emp.id}`)}>
                                        <h4 className="sum-data-hours">{emp.firstName} {emp.lastName}</h4>
                                        <p className="sum-data-hours">Reg: {(totalRegular / 60).toFixed(2)} hrs</p>
                                        <p className="sum-data-hours">OT: {(totalOvertime / 60).toFixed(2)} hrs</p>
                                        <p className="sum-data-hours">PTO Used: {(ptoHoursInRangeByEmployee[emp.id] ?? 0).toFixed(2)} hrs</p>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                <section className="panel">
                    <h3>Pto this year</h3>
                    <div className="hours-summary-grid">
                        {employees.map((emp) => {
                            let empPto = 0
                            let empHours = 0
                            let hoursWorked = emp.accruedMinutesYTD ? emp.accruedMinutesYTD / 60 : 0
                            const hoursAllowed = hoursWorked * emp.ptoRatio
                            if (ptoDaysByEmployee[emp.id]) {
                                empPto = ptoDaysByEmployee[emp.id]
                                empHours = ptoHoursByEmployee[emp.id]
                            }
                            // const ptoAllowed = calculatePtoDaysAllowed(workedMinutes, emp.ptoAllowed ?? 0);
                            return (
                                <div
                                    key={emp.id}
                                    className="employee-pto"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(`/employees/${emp.id}/pto`)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            navigate(`/employees/${emp.id}/pto`);
                                        }
                                    }}
                                >
                                    <h4 className="sum-data-header">{emp.firstName} {emp.lastName}</h4>
                                    <p className="sum-data">Taken: {formatPtoDays(empPto)} days | {empHours} hours</p>
                                    <p className="sum-data">Accrued: {hoursAllowed.toFixed(2)} hours</p>
                                    <p className="sum-data">Available: {(hoursAllowed - empHours).toFixed(2)} hours</p>
                                </div>
                            );
                        })}
                    </div>

                </section>
            </main>
        </div>
    );
};

require('./styles.scss');
